import React, { useState, useContext } from 'react';
import Styled from 'styled';
import { useMutation } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import { move } from 'formik';
import { useHistory } from 'react-router';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import CollectionView from 'lib/collection/collection-view';
import { AlertContext } from 'lib/alert-context/alert-context';

import PhotoFileUpload from 'four-nets/photoblog/posts/album/detail/photo-file-upload';
import UploadPhotofile from 'four-nets/ui-v2/form/upload-photofile/upload-photofile';
import iconPhotoblogs from 'four-nets/icons/svg/icon_photoblogs.svg';
import Spinner from 'four-nets/ui/spinner/spinner';
import Modal from 'four-nets/ui-v2/modal/modal';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import useWriteImagesCache from '../../use-write-images-cache/use-write-images-cache';
import { FIRST } from '../../detail/detail-loader';
import mutation, { AddPostImagesMutation, AddPostImagesMutationVariables } from './mutations/add-post-images.graphql';
import defaultStyles from './add-images.scss';

interface Props {
    id: string;
    href: string;
    styles: typeof defaultStyles;
}

const Image: React.FC<{ photoFileUpload: PhotoFileUpload }> = ({ photoFileUpload }) => {
    const [photofile, setPhotoFile] = useState<PhotoFileUpload['photofile'] | null>(null);

    const onPhotoFile = (photoFile: PhotoFileUpload['photofile']) => {
        photoFileUpload.setPhotoFile(photoFile);
        setPhotoFile(photoFile);
    };

    return (
        <UploadPhotofile
            automaticUpload={true}
            file={photoFileUpload.file}
            url={photoFileUpload.url}
            onPhotoFile={onPhotoFile}
        >
            {!photofile ? <Spinner style={{ spinner: defaultStyles.spinner }} /> : null}
        </UploadPhotofile>
    );
};

const AddImages: React.FC<Props> = props => {
    const { fail } = useContext(AlertContext);

    const history = useHistory();
    const writeImagesCache = useWriteImagesCache();

    const { styles, id } = props;

    const [photoFileUploads, setPhotoFileUploads] = useState<PhotoFileUpload[]>([]);
    const [loading, setLoading] = useState(false);
    const [key, setKey] = useState(Math.random());

    const [addPostImages] = useMutation<AddPostImagesMutation, AddPostImagesMutationVariables>(mutation);

    const moveFn = (from: number, to: number) => {
        setPhotoFileUploads(move(photoFileUploads, from, to));
    };
    const itemKey = (element: PhotoFileUpload) => element.key;

    const element = (element: PhotoFileUpload) => {
        return <Image photoFileUpload={element} />;
    };

    const submit = () => {
        if (photoFileUploads.length > photoFileUploads.filter(element => element.isValid()).length) {
            fail!({
                body: <FormattedMessage id='WAIT_TILL_IMAGES_ARE_UPLOADED' />,
                onAccept: () => {
                    /* pass*/
                },
            });
            return;
        }

        setLoading(true);
        return addPostImages({
            variables: {
                input: {
                    postId: id,
                    images: photoFileUploads
                        .filter(element => element.isValid())
                        .map(element => ({ photoFileId: element.photofile!.id })),
                },
            },
        })
            .then(result => {
                if (result.data?.addPostImages?.post?.images?.edges.length) {
                    writeImagesCache(result.data.addPostImages.post.id, result.data.addPostImages.post.images.edges);
                }
            })
            .then(() => {
                setPhotoFileUploads([]);
                history.push({
                    state: { ommitFirst: true },
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <div className={styles.addPhotos}>
                <div className={styles.addPhotosLabel}>
                    <SVGIcon style={{ image: styles.icon }} src={iconPhotoblogs} />
                    <FormattedMessage id='ALBUM_ADD_PHOTOS' />
                </div>
                <input
                    key={key}
                    className={styles.input}
                    accept='image/gif, image/jpeg, image/jpg, image/png' // we do not accept .svg images
                    multiple={true}
                    onChange={event => {
                        if (event.target.files) {
                            const photoFileUploads: PhotoFileUpload[] = [];
                            for (const file of event.target.files as any) {
                                photoFileUploads.push(new PhotoFileUpload(file));
                            }

                            setPhotoFileUploads(photoFileUploads);
                        }
                    }}
                    type='file'
                />
            </div>
            <Modal
                onAccept={submit}
                title={<FormattedMessage id='ALBUM_ADD_PHOTOS' />}
                open={Boolean(photoFileUploads && photoFileUploads.length)}
                dirty={true}
                onClose={() => {
                    const onAccept = () => {
                        setKey(key + 1);
                        setPhotoFileUploads([]);
                    };

                    // if (!submitted) {
                    //     confirm!({
                    //         body: (
                    //             <>
                    //                 <FormattedMessage id='LEAVE_WITHOUT_SAVING' />?
                    //             </>
                    //         ),
                    //         onAccept,
                    //         accept: <FormattedMessage id='LEAVE' />,
                    //     });
                    // } else {
                    onAccept();
                    // }
                }}
            >
                <LoadingWrapperView loading={loading}>
                    <div className={styles.images}>
                        <CollectionView
                            center={false}
                            items={photoFileUploads}
                            element={element}
                            move={moveFn}
                            isDraggable={() => true}
                            width={90}
                            height={90}
                            itemKey={itemKey}
                        />
                    </div>
                </LoadingWrapperView>
            </Modal>
        </>
    );
};

export default Styled(defaultStyles)(AddImages);
